import { useTranslation } from 'react-i18next';
import { FC, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ColumnDef,
  PaginationState,
  SortingState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useHistoryStateValue } from '@pcmp/react-providers';
import Button from '@ingka/button';
import arrowRightIcon from '@ingka/ssr-icon/paths/arrow-right';
import warningIcon from '@ingka/ssr-icon/paths/warning-triangle-orange-small';
import Checkbox from '@ingka/checkbox';
import SSRIcon from '@ingka/ssr-icon';
import Tooltip from '@ingka/tooltip';

import { CodeV2 as Code } from '@/redux/apis/codes-api';
import { User } from '@/redux/apis/frontend-api';
import { useGetDebouncedCodeSearch } from '@/redux/features/code-search/hooks';
import { useGetQrCodesWithFilters } from './data';
import { SkapaTable } from '@/components/data/skapa-table';
import { LoadingState } from '@/components/helpers/loading/index';

const PAGE_SIZE = 8;
const DEBOUNCE_DELAY = 250;

type CarrierValue = {
  type?: string[];
  size?: string[];
};

const FilterCodesTableUI: FC<{
  codes: Code[];
  setSelectedCodes?: (codes: Code[]) => void;
}> = ({ codes, setSelectedCodes }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<Code>[]>(
    () => [
      {
        id: 'select-col',
        accessorFn: ({ slug }) => slug,
        cell: (info) => {
          const value = info.getValue() as string;

          return (
            <Checkbox
              id={`checkbox-${value}`}
              value="checked"
              checked={info.row.getIsSelected()}
              disabled={!info.row.getCanSelect()}
              onChange={info.row.getToggleSelectedHandler()}
            />
          );
        },
        header: () => '',
        enableSorting: false,
        meta: {
          headerClassName: 'max-w-[20px] text-right',
        },
      },
      {
        id: 'codeName',
        accessorFn: (value) => value.codeName,
        cell: (info) => {
          const value = info.getValue() as string;

          return (
            <Link
              data-testid="name"
              className="underline"
              to={`/codes/${info.row.original.slug}`}
            >
              {value}
            </Link>
          );
        },
        header: () => t('label.code.name'),
        enableSorting: true,
        sortingFn: 'auto',
      },
      {
        id: 'createdBy',
        accessorFn: (value) => value.user.name || value.user.email,
        cell: (info) => {
          const value = info.getValue() as string;

          return (
            <p className="max-w-[200px] overflow-ellipsis overflow-hidden whitespace-nowrap">
              {value}
            </p>
          );
        },
        header: () => t('label.createdBy'),
        enableSorting: true,
        sortingFn: 'auto',
      },
      {
        id: 'component',
        accessorFn: (value) =>
          value.store?.component
            ?.map((v) => t(`label.components.${v}`))
            .join(', '),
        cell: (info) => {
          const value = info.getValue() as string;

          return (
            <p className="max-w-[200px] overflow-ellipsis overflow-hidden whitespace-nowrap">
              {value}
            </p>
          );
        },
        header: () => t('label.component'),
        enableSorting: true,
        sortingFn: 'auto',
        meta: {
          headerClassName: 'whitespace-nowrap',
        },
      },
      {
        id: 'carrierType',
        accessorFn: (value) =>
          value.carrier?.type
            ?.map((v) => t(`label.carrierTypes.${v}`))
            .join(', '),
        cell: (info) => {
          const value = info.getValue() as string;

          return (
            <p className="max-w-[200px] overflow-ellipsis overflow-hidden whitespace-nowrap">
              {value}
            </p>
          );
        },
        header: () => t('label.carrierType'),
        enableSorting: true,
        sortingFn: 'auto',
        meta: {
          headerClassName: 'whitespace-nowrap',
        },
      },
      {
        id: 'carrierSize',
        accessorFn: (value) =>
          value.carrier?.size
            ?.map((v) => t(`label.carrierSizes.${v}`))
            .join(', '),
        cell: (info) => {
          const value = info.getValue() as string;

          return (
            <p className="max-w-[200px] overflow-ellipsis overflow-hidden whitespace-nowrap">
              {value}
            </p>
          );
        },
        header: () => t('label.carrierSize'),
        enableSorting: true,
        sortingFn: 'auto',
        meta: {
          headerClassName: 'whitespace-nowrap',
        },
      },
      {
        id: 'scans',
        accessorFn: (value) => value.scans,
        cell: (info) => {
          const value = info.getValue() as string;

          return <p className="md:min-w-0 font-bold">{value}</p>;
        },
        header: () => t('label.code.scans'),
        enableSorting: true,
        sortingFn: 'auto',
        meta: {
          headerClassName: 'max-w-[50px]',
        },
      },
      {
        id: 'actions',
        accessorFn: ({ carrier }) => carrier,
        cell: (info) => {
          const carrier = info.getValue() as CarrierValue;

          return (
            <div className="text-center">
              {(!carrier?.type?.length || !carrier?.size?.length) && (
                <Tooltip
                  interactiveCustomTrigger
                  position="top"
                  tooltipText={t('page.codes.actions.carrier')}
                >
                  <SSRIcon paths={warningIcon} />
                </Tooltip>
              )}
            </div>
          );
        },
        header: () => t('page.codes.action'),
        enableSorting: false,
        meta: {
          headerClassName: 'text-center',
        },
      },
      {
        id: 'link',
        accessorFn: ({ slug }) => slug,
        cell: (info) => {
          const value = info.getValue() as string;

          return (
            <div className="text-right">
              <Button
                iconOnly
                aria-label={t('page.marketDetail.stores.edit')}
                size="xsmall"
                ssrIcon={arrowRightIcon}
                className="ml-1 text-right"
                data-testid="viewBtn"
                type="tertiary"
                onClick={() => navigate(`/codes/${value}`)}
              />
            </div>
          );
        },
        header: () => '',
        enableSorting: false,
        meta: {
          headerClassName: 'max-w-[20px] text-right',
        },
      },
    ],
    [navigate, t]
  );
  const [pagination, setPagination] = useHistoryStateValue<PaginationState>(
    'codesTablePagination',
    {
      pageIndex: 0,
      pageSize: PAGE_SIZE,
    }
  );
  const [sorting, setSorting] = useHistoryStateValue<SortingState>(
    'codesTableSorting',
    []
  );
  const search = useGetDebouncedCodeSearch(DEBOUNCE_DELAY);
  const table = useReactTable({
    data: codes,
    columns,
    getRowId: (row) => row.slug ?? '',
    state: {
      sorting,
      pagination,
      globalFilter: search.text,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: true,
    enableMultiRowSelection: true,
  });
  const maxResults = table.getState().pagination.pageSize;
  const totalResults = table.getPrePaginationRowModel().rows.length;
  const hasMorePages = totalResults > maxResults;

  const rowSelection = table.getState().rowSelection;
  useEffect(() => {
    const selectedCodeSlugs = new Set(
      Object.keys(rowSelection).filter((rowId) => rowSelection[rowId])
    );

    const selectedCodesBySlug = codes.filter((code) =>
      selectedCodeSlugs.has(code.slug ?? '')
    );

    setSelectedCodes?.(selectedCodesBySlug);
  }, [codes, rowSelection, setSelectedCodes]);

  return (
    <div>
      <SkapaTable table={table} />

      {hasMorePages && (
        <div className="w-full flex flex-col justify-center items-center mt-6">
          <div className="w-[164px]">
            <Button
              data-testid="loadMoreCodes"
              iconPosition="leading"
              text={t('label.loadMore')}
              type="secondary"
              size="small"
              fluid
              onClick={() => table.setPageSize(pagination.pageSize + PAGE_SIZE)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const CodesTable: FC<{
  user: User;
  setSelectedCodes: (codes: Code[]) => void;
}> = ({ user, setSelectedCodes }) => {
  const {
    data: codesData = [],
    isLoading,
    isFetching,
  } = useGetQrCodesWithFilters(user, { debounce: DEBOUNCE_DELAY });
  const loading = isLoading || isFetching;

  if (loading)
    return (
      <div className="flex flex-col flex-grow w-full h-full items-center justify-center mt-6 sm:mt-0">
        <LoadingState />
      </div>
    );

  return (
    <div className="w-full mt-6">
      <FilterCodesTableUI
        codes={codesData}
        setSelectedCodes={setSelectedCodes}
      />
    </div>
  );
};
